"use client";

import { usePathname } from "next/navigation";
import { useMemo } from "react";
import Button from "@/components/common/Button";
import { PATH } from "@/constants/path";
import { useBatchImpressionTracking } from "@/hooks/useImpressionTracking";
import { useScrollDepthTracking } from "@/hooks/useScrollDepthTracking";
import { trackEvent } from "@/utils/analytics";
import PricingCard from "./PricingCard";

function PricingSection() {
  useScrollDepthTracking();

  const path = usePathname();

  const impressionEvents = useMemo(
    () => [
      {
        eventName: "cta_impression",
        params: { cta_type: "case_study", page_location: "/pricing/" },
      },
      {
        eventName: "cta_impression",
        params: { cta_type: "inquiry", cta_text: "지금 바로 요청하기", plan: "list" },
      },
      {
        eventName: "cta_impression",
        params: { cta_type: "inquiry", cta_text: "도입 문의하기", plan: "subscription" },
      },
    ],
    [],
  );

  const sectionRef = useBatchImpressionTracking<HTMLDivElement>(impressionEvents);

  const handleListPlanClick = () => {
    trackEvent("cta_click", path, {
      cta_type: "inquiry",
      cta_text: "지금 바로 요청하기",
      plan: "list",
    });
  };

  const handleSubscriptionPlanClick = () => {
    trackEvent("cta_click", path, {
      cta_type: "inquiry",
      cta_text: "도입 문의하기",
      plan: "subscription",
    });
  };

  const handleCaseStudyClick = () => {
    trackEvent("cta_click", path, {
      cta_type: "case_study",
      page_location: "/pricing/",
    });
  };

  return (
    <div ref={sectionRef} className="flex flex-col gap-10 py-32 tablet:gap-12">
      <div id="pricing">
        <div className="mb-2 text-center text-18">스타트업부터 대기업까지 서치라이트AI와 함께</div>
        <div className="text-center text-30 font-semibold">
          기업이 찾던 핵심 인재 발굴,
          <br />
          AI 채용을 더 스마트하게
        </div>
      </div>

      <div
        className="mx-auto grid w-full max-w-[1000px] grid-cols-1 gap-x-6 gap-y-3 px-side tablet:grid-cols-[4fr_3fr]">
        <PricingCard>
          <div>
            <div className="mb-6">
              <PricingCard.Title className="mb-2" title="리스트형 상품" />
              <PricingCard.Description
                description={`채용이 어려웠던 포지션, 커피챗 컨택 등\n기업에게 꼭 맞는 핵심 인재 리스트가 필요한 기업`}
              />
            </div>
            <div className="mb-8 flex items-end justify-between">
              <PricingCard.Price price={100} />
              <PricingCard.DownloadCompanyIntroButton onClick={handleCaseStudyClick} />
            </div>
            <Button href={PATH.REQUEST} className="inline-block w-full" onClick={handleListPlanClick}>
              지금 바로 요청하기
            </Button>
          </div>
          <PricingCard.Features
            features={[
              "고객사 채용 프로젝트 관리 플랫폼 제공",
              "AI 개인화 메시지 생성 툴 포함",
              "후보자 역량 정의",
              "후보자 리스트 50명",
              "추천 순위, 경험 역량 Tag 역량 인사이트 제공",
              "컨택을 위한 개인화 메시지 템플릿 제공",
            ]}
          />
        </PricingCard>

        <PricingCard>
          <div>
            <div className="mb-6">
              <PricingCard.Title className="mb-2" title="구독형 상품" />
              <PricingCard.Description description={`AI 기반 다이렉트 소싱에 최적화 된\n통합 리크루팅 솔루션`} />
            </div>
            <div className="mb-8 flex items-end justify-between">
              <div className="text-28 font-semibold leading-none">준비중</div>
            </div>
            <Button href={PATH.REQUEST} className="inline-block w-full" onClick={handleSubscriptionPlanClick}>
              도입 문의하기
            </Button>
          </div>
          <PricingCard.Features features={["AI 통합 리크루팅 솔루션 제공", "최대 팀 멤버 5명까지 추가 ", "14일 무료 데모"]} />
        </PricingCard>
      </div>
    </div>
  );
}

export default PricingSection;
